import { Alert, Button, Input, InputNumber, Space, message } from "antd";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import {
    DatePicker as ZhDatePicker,
    DetailHeader as ZhDetailHeader,
    Select as ZhSelect,
    Upload as ZhUpload,
    type UploadRequestOptions
} from "zhihao-ui-react";

import request from "@/api";
import type { InvoiceForm } from "@zhihaoscm/entity";
import RequiredMark from "@/views/components/RequiredMark";
import "./TradeInvoiceFormPage.scss";

interface InvoiceFormState {
    orderId: string;
    receivePaymentId: string;
    amount: number | null;
    invoiceDate: string;
    fileId: number | null;
}

export interface TradeInvoiceFormPageProps {
    addInvoice?: (form: InvoiceForm) => Promise<unknown>;
}

/** 复用 Vue 发票新增接口，保留发票表单的核心提交契约。 */
export async function addTradeInvoice(form: InvoiceForm): Promise<unknown> {
    const response = await request.service.post("/admin/invoice", form);
    return response.data;
}

const initialForm: InvoiceFormState = {
    orderId: "",
    receivePaymentId: "",
    amount: null,
    invoiceDate: "",
    fileId: null
};

const INVOICE_FILE_MAX_SIZE = 10 * 1024 * 1024;
const INVOICE_FILE_EXTENSIONS = new Set(["pdf", "ofd", "jpeg", "jpg", "png", "xml", "xls", "xlsx"]);
const INVOICE_FILE_TYPES = new Set([
    "application/pdf",
    "application/ofd",
    "image/jpeg",
    "image/png",
    "application/xml",
    "text/xml",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
]);

/** 保留 Vue 发票规则：今天之后的开票日期不可选。 */
function disableInvoiceDateAfterToday(date: Date): boolean {
    return date.getTime() > Date.now();
}

/** 发票新增页，先覆盖 Vue 表单的核心字段、校验和提交 payload。 */
export function TradeInvoiceFormPage({ addInvoice = addTradeInvoice }: TradeInvoiceFormPageProps) {
    const navigate = useNavigate();
    const [form, setForm] = useState<InvoiceFormState>(initialForm);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    /** 按 Vue single-upload 契约上传发票单据，并将返回文件 ID 回填到提交字段。 */
    const validateInvoiceFile = (file: File): boolean => {
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        if (!INVOICE_FILE_EXTENSIONS.has(extension) || (file.type && !INVOICE_FILE_TYPES.has(file.type))) {
            setErrorMessage("发票单据仅支持 PDF、OFD、JPEG、PNG、XML、Excel 格式");
            return false;
        }
        if (file.size > INVOICE_FILE_MAX_SIZE) {
            setErrorMessage("发票单据大小不能超过10MB");
            return false;
        }
        return true;
    };

    /** 复用 Vue SingleUpload 的自定义上传请求，并把文件 ID 回填到表单。 */
    const uploadInvoiceFile = async ({ file, onError, onSuccess }: UploadRequestOptions) => {
        try {
            const invoiceFile = file as File;
            const formData = new FormData();
            formData.append("file", invoiceFile);
            formData.append("fileName", invoiceFile.name);
            // 懒加载上传服务，避免单据上传模块影响表单首屏加载。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            const fileId = Number(uploadedFile.id);
            if (!Number.isInteger(fileId) || fileId <= 0) throw new Error("invalid file id");
            setForm(current => ({ ...current, fileId }));
            setErrorMessage(null);
            onSuccess?.(uploadedFile);
        } catch {
            setErrorMessage("发票单据上传失败");
            onError?.(new Error("发票单据上传失败"));
        }
    };

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!form.orderId.trim()) {
            setErrorMessage("请选择关联订单");
            return;
        }
        if (!form.receivePaymentId.trim()) {
            setErrorMessage("请选择关联收款单");
            return;
        }
        if (form.amount == null || !Number.isFinite(form.amount) || form.amount <= 0) {
            setErrorMessage("请输入开票金额");
            return;
        }
        if (!form.invoiceDate) {
            setErrorMessage("请选择开票日期");
            return;
        }
        if (form.fileId == null || !Number.isInteger(form.fileId) || form.fileId <= 0) {
            setErrorMessage("请上传发票单据");
            return;
        }

        try {
            await addInvoice({
                orderId: form.orderId.trim(),
                relatedReceiveInfo: [
                    {
                        receivePaymentId: form.receivePaymentId.trim(),
                        invoiceAmount: form.amount
                    }
                ],
                amount: form.amount,
                invoiceDate: form.invoiceDate,
                fileId: form.fileId
            });
            setErrorMessage(null);
            message.success("新增成功");
        } catch {
            message.error("发票新增失败");
        }
    };

    return (
        <section aria-label="trade-invoice-form-page" className="trade-invoice-form-page">
            <section className="section-card trade-invoice-form__header">
                <ZhDetailHeader title="新增发票" />
            </section>
            <section className="section-card trade-invoice-form__section">
                <div className="section-title">关联信息</div>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <form onSubmit={event => void submit(event)}>
                    <Space orientation="vertical" size="middle" className="trade-invoice-form__fields">
                        <label>
                            <span>
                                <RequiredMark />
                                关联订单
                            </span>
                            <ZhSelect
                                aria-label="关联订单"
                                allowClear
                                showSearch
                                options={form.orderId ? [{ label: form.orderId, value: form.orderId }] : []}
                                value={form.orderId}
                                onChange={value => setForm(current => ({ ...current, orderId: String(value || "") }))}
                                onSearch={value => setForm(current => ({ ...current, orderId: value }))}
                                placeholder="请选择关联订单"
                            />
                        </label>
                        <label>
                            <span>
                                <RequiredMark />
                                关联收款单
                            </span>
                            <Input
                                aria-label="关联收款单"
                                value={form.receivePaymentId}
                                onChange={event =>
                                    setForm(current => ({ ...current, receivePaymentId: event.target.value }))
                                }
                                placeholder="请输入收款单编号"
                            />
                        </label>
                        <label>
                            <span>
                                <RequiredMark />
                                开票金额
                            </span>
                            <InputNumber
                                aria-label="开票金额"
                                className="w-full"
                                min={0}
                                value={form.amount}
                                onChange={value => setForm(current => ({ ...current, amount: value }))}
                            />
                        </label>
                        <label>
                            <span>
                                <RequiredMark />
                                开票日期
                            </span>
                            <ZhDatePicker
                                aria-label="开票日期"
                                format="YYYY-MM-DD"
                                placeholder="请选择开票日期"
                                value={form.invoiceDate}
                                type="date"
                                valueFormat="YYYY-MM-DD"
                                disabledDate={disableInvoiceDateAfterToday}
                                onChange={(_value, formattedValue) =>
                                    setForm(current => ({
                                        ...current,
                                        invoiceDate: typeof formattedValue === "string" ? formattedValue : ""
                                    }))
                                }
                            />
                        </label>
                        <label className="hidden">
                            发票单据 ID
                            <InputNumber
                                aria-label="发票单据 ID"
                                className="w-full"
                                min={1}
                                value={form.fileId}
                                onChange={value => setForm(current => ({ ...current, fileId: value }))}
                            />
                        </label>
                        <label>
                            <span>
                                <RequiredMark />
                                发票单据
                            </span>
                            <ZhUpload
                                action="#"
                                aria-label="发票单据上传"
                                accept=".pdf,.ofd,.jpeg,.jpg,.png,.xml,.xls,.xlsx,application/pdf,image/jpeg,image/png,application/xml"
                                limit={1}
                                showFileList={false}
                                beforeUpload={validateInvoiceFile}
                                httpRequest={uploadInvoiceFile}
                            >
                                <Button htmlType="button" type="dashed">
                                    上传发票单据
                                </Button>
                            </ZhUpload>
                        </label>
                    </Space>
                    <Space className="operation-fixed-btn" size={0}>
                        <Button aria-label="提交发票" htmlType="submit">
                            提交
                        </Button>
                        <Button htmlType="button" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </form>
            </section>
        </section>
    );
}

export default TradeInvoiceFormPage;
